import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Loader2, Mail, Copy, Download, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscribersAdminPage = () => {
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState('');
  const [pwError, setPwError] = useState('');
  const [loading, setLoading] = useState(false);
  const [subs, setSubs] = useState([]);

  const load = async (pw) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/subscribers?password=${encodeURIComponent(pw)}`);
      setSubs(res.data.subscribers || []);
      setAuthed(true);
      setPwError('');
    } catch (err) {
      if (err.response?.status === 401) { setPwError('Wrong password'); setAuthed(false); }
      else setPwError('Failed to load: ' + (err.message || 'unknown'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (e) => { e.preventDefault(); if (password) load(password); };

  const copyEmails = async () => {
    const text = subs.map((s) => s.email).join(', ');
    try { await navigator.clipboard.writeText(text); toast.success(`Copied ${subs.length} emails`); }
    catch { toast.error('Copy failed'); }
  };

  const downloadCsv = () => {
    const rows = [['email', 'source', 'dog_slug', 'created_at'], ...subs.map((s) => [s.email, s.source || '', s.dog_slug || '', s.created_at || ''])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `vent-subscribers-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  if (!authed) {
    return (
      <div className="min-h-screen bg-vent-bg bg-pattern flex items-center justify-center px-5">
        <form onSubmit={handleLogin} className="w-full max-w-sm bg-zinc-900 border border-vent-secondary/20 rounded-2xl p-6" data-testid="subs-login-form">
          <div className="flex items-center gap-2 mb-4 text-white">
            <Lock className="w-5 h-5 text-emerald-400" />
            <h1 className="font-heading font-bold text-xl">Subscribers</h1>
          </div>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Admin password"
            data-testid="subs-password-input"
            className="w-full bg-black/40 border border-white/10 rounded-full px-4 py-3 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-emerald-400/50 mb-3"
          />
          {pwError && <p className="text-rose-400 text-xs mb-3" data-testid="subs-pw-error">{pwError}</p>}
          <button type="submit" disabled={loading} data-testid="subs-login-btn" className="w-full py-3 rounded-full bg-emerald-400 text-[#08130d] font-heading font-bold text-sm disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Enter'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-vent-bg bg-pattern px-4 sm:px-6 py-8" data-testid="subs-admin">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-2 text-white">
            <Mail className="w-6 h-6 text-emerald-400" />
            <h1 className="font-heading font-bold text-2xl">Subscribers</h1>
            <span className="ml-2 text-sm text-zinc-400" data-testid="subs-count">{subs.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => load(password)} className="flex items-center gap-1.5 px-3 py-2 rounded-full glass border border-white/10 text-zinc-300 text-xs" data-testid="subs-refresh-btn"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
            <button onClick={copyEmails} disabled={!subs.length} className="flex items-center gap-1.5 px-3 py-2 rounded-full glass border border-white/10 text-zinc-300 text-xs disabled:opacity-40" data-testid="subs-copy-btn"><Copy className="w-3.5 h-3.5" /> Copy emails</button>
            <button onClick={downloadCsv} disabled={!subs.length} className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-emerald-400 text-[#08130d] font-semibold text-xs disabled:opacity-40" data-testid="subs-csv-btn"><Download className="w-3.5 h-3.5" /> CSV</button>
          </div>
        </div>

        {subs.length === 0 ? (
          <div className="glass border border-white/10 rounded-2xl p-10 text-center text-zinc-500" data-testid="subs-empty">No subscribers yet.</div>
        ) : (
          <div className="glass border border-white/10 rounded-2xl overflow-hidden">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-zinc-500 text-xs uppercase tracking-wider border-b border-white/10">
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3 hidden sm:table-cell">Source</th>
                  <th className="px-4 py-3 hidden sm:table-cell">Dog</th>
                  <th className="px-4 py-3">When</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s, i) => (
                  <tr key={s.id || i} className="border-b border-white/5 last:border-0" data-testid={`subs-row-${i}`}>
                    <td className="px-4 py-3 text-white">{s.email}</td>
                    <td className="px-4 py-3 text-zinc-400 hidden sm:table-cell">{s.source || '—'}</td>
                    <td className="px-4 py-3 text-zinc-400 hidden sm:table-cell">{s.dog_slug || '—'}</td>
                    <td className="px-4 py-3 text-zinc-500">{fmt(s.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscribersAdminPage;
